import { Dialog, DialogProps, styled } from '@mui/material';
import React, { useCallback, useState } from 'react';
import Flex from 'src/components/BeaconV2Components/Flex/Flex';
import { Text } from 'src/components/BeaconV2Components/Generic/Text';
import { ExitIconSmall } from 'src/components/SvgIcons';
import { SlButton } from 'src/components/BeaconV2Components/Header/SLDropdownMenu/SlButton';
import { SlDropdownMenu } from 'src/components/BeaconV2Components/Header/SLDropdownMenu/input';
import { useEntityInfo } from 'src/components/BeaconV2Components/ExperimentalLayout/Forecasts/BulkAdjustments/hooks';
import { planTypeDropdownOptions } from 'src/components/BeaconV2Components/ExperimentalLayout/Forecasts/BulkAdjustments/BulkTemplateUploadModal';
import axios from 'axios';
import { useAppSelector, useHistory, useSnackbar } from 'src/utils/Hooks';
import { usePollingContext } from 'src/providers/PollingContextProvider';
import { useBeaconClientId } from 'src/utils/Hooks/reduxSelectors';
import BulkTemplateDownloadModalLoadingInner from 'src/components/BeaconV2Components/ExperimentalLayout/Forecasts/BulkAdjustments/BulkTemplateDownloadModalLoadingInner';
import useServerProxy from 'src/components/BeaconV2Components/ExperimentalLayout/Forecasts/BulkAdjustments/hooks/useServerProxy';
import useNotificationsBadge from 'src/utils/Hooks/useNotificationsBadge';
import { AdjustmentDataItem } from 'src/components/BeaconV2Components/ExperimentalLayout/Settings/utils';
import { PlanTypeOption } from 'src/components/BeaconV2Components/ExperimentalLayout/Forecasts/RefactoredAdjustmentModals/constants';
import { trackMixpanelEvent } from 'src/utils/mixpanel';

export const GenericStyledDialogue = styled(Dialog)({
  '& .MuiPaper-root': {
    width: '752px',
    height: '641px',
    maxWidth: 'none',
    boxShadow: '0 0 16px 0 rgba(0, 0, 0, 0.06)',
    border: 'solid 1px #dedede',
    backgroundColor: '#fff',
    borderRadius: '8px',
    padding: '0',
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden'
  }
});

// TODO: Extend this when segments are added
export enum bulkAdjustmentLevels {
  client = 'Org',
  category = 'Category',
  subcategory = 'Subcategory',
  brand = 'Brand',
  segment = 'Segment',
  businessunit = 'BusinessUnit',
  product = 'Sku'
}

interface BulkTemplateDownloadModalProps extends DialogProps {
  open: boolean;
  onClose: () => void;
  initialPlanType?: PlanTypeOption;
}

// poll interval and add percentage to 1 for every 20 secs. Apple org takes around 80% then goes to 100%
const POLL_INTERVAL_FOR_DOWNLOAD_TEMPLATE = 2000;

const MODAL_CLOSING_TIME = 5000;
/**
 *
 * This component is a modal that is used for adding bulk adjustment for any entityType.
 */
// eslint-disable-next-line react/display-name
export const BulkTemplateDownloadModal = React.forwardRef(
  (props: BulkTemplateDownloadModalProps, ref: React.Ref<HTMLDivElement>): JSX.Element => {
    const entityInfo = useEntityInfo();
    const [selectedOption, setSelectedOption] = useState(
      (props.initialPlanType && planTypeDropdownOptions.find((option) => option.id === props.initialPlanType)) ||
      planTypeDropdownOptions[0]
    );
    const retailerId = useAppSelector((state) => state.retailer.id);
    const { userId, divisionId, companyId } = useAppSelector((state) => state.user.session);
    const {
      type: entityType,
      id: entityId,
      name: entityName,
      retailerSku
    } = useAppSelector((state) => state.entityService.mainEntity);
    const { startPolling, stopPolling } = usePollingContext();
    const { setAdjustmentBadge } = useNotificationsBadge();
    const [disabledDownload, setDisabledDownload] = useState(false);
    const [isDownloadInitiated, setIsDownloadInitiated] = useState(false);
    const beaconClientId = useBeaconClientId();
    const { showSnackbar } = useSnackbar();
    const [downloadPercentage, setDownloadPercentage] = useState(0);
    const history = useHistory();
    const { searchParams } = useAppSelector((state) => state.app.queryParams);
    const { pollBulkAdjustments } = useServerProxy();

    const onCloseModal = useCallback(() => {
      props.onClose();
      // prevent the modal animation delays closing, and flicking the download button
      setTimeout(() => {
        // TODO:: Check a user can access the processing modal from settings, if so, we should check polling map.
        setIsDownloadInitiated(false);
        setDisabledDownload(false);
      }, 500);
    });

    const handlingPolling = useCallback(
      (
        processingStatus: AdjustmentDataItem['processingStatus'],
        validatePollingId: string,
        bulkAdjustmentId: string
      ) => {
        setDownloadPercentage((prevPercentage) => {
          return Math.min(prevPercentage + 1, 90);
        });

        if (processingStatus === 'Success') {
          stopPolling(validatePollingId);
          setDownloadPercentage(100);
          setTimeout(() => {
            onCloseModal();
            showSnackbar({
              message: 'Bulk template successfully downloaded.',
              type: 'success',
              showButton: true,
              buttonProps: {
                text: 'View',
                onClick: () => {
                  history.push(`/account/downloads${searchParams}&tab=adjustmentsDownload`);
                }
              }
            });
          }, MODAL_CLOSING_TIME);
          setAdjustmentBadge(true);
        }
        if (processingStatus === 'Error') {
          stopPolling(validatePollingId);
          onCloseModal();
          showSnackbar({
            message: 'Download failed. We have encountered an issue processing your request. Please try again.',
            type: 'error',
            showButton: false
          });
          console.error('Error downloading bulk template bulkAdjustmentId is - ', bulkAdjustmentId);
        }
      },
      [history, onCloseModal, searchParams, setAdjustmentBadge, showSnackbar, stopPolling]
    );

    const requestBody = {
      userId,
      planType: selectedOption.id,
      beaconClientId,
      retailerId: Number(retailerId),
      bulkAdjustmentLevel: bulkAdjustmentLevels[entityType],
      bulkAdjustmentLevelId:
        entityType === 'segment'
          ? JSON.stringify({
            company_id: companyId,
            division_id: divisionId,
            segment_id: entityId
          })
          : entityType === 'product'
            ? retailerSku
            : entityId
    };

    const onDownloadBtnClick = async () => {
      trackMixpanelEvent({
        eventName: 'download bulk adjustments template',
        data: {
          planType: selectedOption.label,
          entityLevel: entityType,
          entityId: `${entityId}`,
          entityName
        }
      });
      setIsDownloadInitiated(true);
      setDisabledDownload(true);

      const response = await axios.post('/api/beaconbulkadjustment/DownloadBulkAdjustmentTemplate', requestBody);
      if (response && response.data && response.data.length > 0) {
        const { bulkAdjustmentId } = response.data[0];
        const validatePollingId = `BULK_ADJ_DOWNLOAD_${bulkAdjustmentId}`;

        startPolling(
          validatePollingId,
          async () => {
            const pollResponse = await pollBulkAdjustments({
              ...requestBody,
              bulkAdjustmentStage: 'Template',
              bulkAdjustmentId
            });

            if (pollResponse && pollResponse[0]) {
              const { processingStatus } = pollResponse[0];
              handlingPolling(processingStatus, validatePollingId, bulkAdjustmentId);
            }
          },
          { interval: POLL_INTERVAL_FOR_DOWNLOAD_TEMPLATE }
        );
      }
    };

    return (
      <GenericStyledDialogue ref={ref} {...props}>
        {isDownloadInitiated ? (
          // TODO:: Design and BE agreed that FE will get the total number of request, and FE will assume the percentage value
          <BulkTemplateDownloadModalLoadingInner
            closeBulkTemplateDownloadModal={onCloseModal}
            downloadPercentage={downloadPercentage}
          />
        ) : (
            <>
              {/* Header */}
              <Flex
                height="72px"
                alignItems="center"
                justifyContent="center"
                style={{
                  borderBottom: '1px solid #E8E8ED',
                  flexShrink: 0,
                  position: 'relative',
                  minHeight: '72px'
                }}
              >
                <Text
                  variant="h4"
                  sx={{ fontSize: '20px', fontWeight: 700, lineHeight: '28px', letterSpacing: '0.08px' }}
                  textAlign="center"
                >
                  Download Template
                </Text>
                <ExitIconSmall
                  onClick={onCloseModal}
                  style={{
                    position: 'absolute',
                    right: '24px',
                    cursor: 'pointer',
                    width: '24px',
                    height: '24px'
                  }}
                />
              </Flex>

              {/* Content */}
              <Flex
                flexDirection="column"
                style={{
                  flex: 1,
                  padding: '24px 48px 0 48px',
                  gap: '24px'
                }}
              >
                <Flex flexDirection="column" gap="sm" style={{ width: '656px' }}>
                  <Text
                    variant="subtitle2"
                    sx={{
                      fontSize: '16px',
                      fontWeight: 500,
                      lineHeight: '24px',
                      letterSpacing: '0.08px'
                    }}
                    textAlign="left"
                  >
                    Create a Template
                  </Text>
                  <Text
                    variant="body2"
                    sx={{
                      fontSize: '14px',
                      fontWeight: 400,
                      lineHeight: '20px',
                      letterSpacing: '0.08px'
                    }}
                    textAlign="left"
                  >
                    Create a bulk adjustment template for the following:
                  </Text>
                </Flex>

                <Flex alignItems="center" style={{ gap: '16px', width: '656px' }}>
                  <Flex
                    style={{ width: '80px', height: '80px', flexShrink: 0 }}
                    alignItems="center"
                    justifyContent="center"
                  >
                    {entityInfo.header}
                  </Flex>
                  <Flex flexDirection="column" gap="sm" style={{ width: '560px' }}>
                    <Text
                      variant="subtitle2"
                      sx={{
                        fontSize: '16px',
                        fontWeight: 500,
                        lineHeight: '24px',
                        letterSpacing: '0.08px'
                      }}
                      textAlign="left"
                    >
                      {entityInfo.displayName}
                    </Text>
                    <Text
                      variant="body2"
                      sx={{
                        fontSize: '14px',
                        fontWeight: 400,
                        lineHeight: '20px',
                        letterSpacing: '0.08px',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap'
                      }}
                      textAlign="left"
                    >
                      {entityInfo.title}
                    </Text>
                  </Flex>
                </Flex>

                <Flex flexDirection="column" style={{ gap: '16px', width: '384px' }}>
                  <Flex flexDirection="column" gap="sm">
                    <Text
                      variant="subtitle2"
                      sx={{
                        fontSize: '16px',
                        fontWeight: 500,
                        lineHeight: '24px',
                        letterSpacing: '0.08px'
                      }}
                      textAlign="left"
                    >
                      Plan Type
                    </Text>
                    <Text
                      variant="body2"
                      sx={{
                        width: '656px',
                        fontSize: '14px',
                        fontWeight: 400,
                        lineHeight: '20px',
                        letterSpacing: '0.08px'
                      }}
                      textAlign="left"
                    >
                      Create a template for the following plan type:
                    </Text>
                  </Flex>
                  <SlDropdownMenu
                    menuItemTextStyles={{ fontWeight: 'normal' }}
                    stacklineButtonProps={{
                      buttonSx: {
                        width: '198px',
                        justifyContent: 'space-between'
                      },
                      textSx: {
                        fontWeight: 'normal',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap'
                      }
                    }}
                    sx={{ maxWidth: '198px' }}
                    options={planTypeDropdownOptions}
                    defaultLabel="Select a plan type"
                    selectedId={selectedOption.id}
                    onChange={(option) => setSelectedOption(option)}
                  />
                </Flex>
              </Flex>

              {/* Footer */}
              <Flex
                justifyContent="space-between"
                alignItems="center"
                style={{
                  height: '72px',
                  minHeight: '72px',
                  borderTop: '1px solid #E8E8ED',
                  padding: '0 48px',
                  flexShrink: 0
                }}
              >
                <SlButton onClick={onCloseModal} buttonSx={{ width: '84px' }}>
                  Cancel
                </SlButton>
                <SlButton
                  disabled={disabledDownload}
                  onClick={onDownloadBtnClick}
                  variant="contained"
                  buttonSx={{ width: '103px' }}
                >
                  Download
                </SlButton>
              </Flex>
            </>
          )}
      </GenericStyledDialogue>
    );
  }
);
